import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { getLocale } from '@/paraglide/runtime.js';

const twMerge = extendTailwindMerge({
  extend: { theme: { spacing: ['control-sm', 'control-md', 'control-xl'] } },
});

const locales: Record<string, string> = { en: 'en-US', fr: 'fr-FR' };
const defaultDateOptions: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};
const dateFormatters = new Map<string, Intl.DateTimeFormat>();
const numberFormatters = new Map<string, Intl.NumberFormat>();

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatDate(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = defaultDateOptions,
  locale = getLocale(),
): string {
  if (value === null || value === undefined) return '-';
  const date = value instanceof Date ? value : new Date(value);
  const resolvedLocale = locales[locale] ?? locale;
  const key = `${resolvedLocale}:${JSON.stringify(options)}`;
  let formatter = dateFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(resolvedLocale, options);
    dateFormatters.set(key, formatter);
  }
  return formatter.format(date);
}

export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
  locale = getLocale(),
): string {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locales[locale], options);
    numberFormatters.set(key, formatter);
  }
  return formatter.format(value);
}

export function formatDateOnly(value: string): string {
  return formatDate(`${value}T00:00:00Z`, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
